import { useCallback, useMemo, useState } from 'react'

import { getFractionImproperToMixedQuestionData, FractionModel } from './FractionImproperToMixed.utils'

interface UseFractionImproperToMixedReturnType {
  question?: FractionModel
  formValid: boolean
  inputs?: InputModel
  error?: ErrorModel
  handleCheckAnswer: () => void
  onInputChange: (input?: InputTypes, val?: string) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

interface InputModel {
  numerator?: string
  denominator?: string
  quotient?: string
}

interface ErrorModel {
  numerator?: boolean
  denominator?: boolean
  quotient?: boolean
}

type InputTypes = 'numerator' | 'denominator' | 'quotient'

function useFractionImproperToMixed({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): UseFractionImproperToMixedReturnType {
  // eslint-disable-next-line
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [error, setError] = useState<ErrorModel>()
  const { question, answer, maxVariation } = useMemo(
    () => getFractionImproperToMixedQuestionData(difficultyLevel, taskHistory),
    [difficultyLevel, taskHistory],
  )

  const [inputs, setInputs] = useState<InputModel>({})

  const handleCheckAnswer = useCallback((): void => {
    const { numerator = '0', denominator = '0', quotient = '0' } = inputs
    const err: ErrorModel = {
      numerator: parseInt(numerator) !== answer.numerator,
      denominator: parseInt(denominator) !== answer.denominator,
      quotient: parseInt(quotient) !== answer.quotient,
    }
    onAnswerStateChange(!(err.numerator || err.denominator || err.quotient))
    setError(err)
  }, [onAnswerStateChange, inputs, answer])

  const onInputChange = useCallback(
    (input?: InputTypes, val?: string) => {
      switch (input) {
        case 'numerator':
          setInputs({ ...inputs, numerator: val })
          break
        case 'denominator':
          setInputs({ ...inputs, denominator: val })
          break
        case 'quotient':
          setInputs({ ...inputs, quotient: val })
          break
        default:
          setInputs({})
      }
    },
    [inputs],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) =>
      [`${question.numerator}-${question.denominator}`, ...prevState].slice(0, maxVariation * 0.7),
    )
    onInputChange()
    onNextQuestionClick()
  }, [onInputChange, onNextQuestionClick, maxVariation, question])

  return {
    question,
    formValid: !!inputs.numerator && !!inputs.denominator && !!inputs.quotient,
    inputs,
    error,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  }
}

export default useFractionImproperToMixed
